/**
 * Advanced Logs API Functions
 * Security, Sessions, Performance, Analytics
 */

import api from './api';

// ============================================
// SECURITY API
// ============================================

// Buscar tentativas de login
export const fetchLoginAttempts = async (limit = 50, onlyFailed = false) => {
    try {
        const params = new URLSearchParams();
        params.append('limit', limit);
        if (onlyFailed) params.append('onlyFailed', 'true');

        const response = await api.get(`/api/logs/security/login-attempts?${params.toString()}`);
        return response.data;
    } catch (error) {
        console.error('Erro ao buscar tentativas de login:', error);
        throw error;
    }
};

// Buscar alertas do sistema
export const fetchAlerts = async (limit = 50) => {
    try {
        const response = await api.get(`/api/logs/security/alerts?limit=${limit}`);
        return response.data;
    } catch (error) {
        console.error('Erro ao buscar alertas:', error);
        throw error;
    }
};

// Marcar alerta como lido
export const markAlertAsRead = async (alertId) => {
    try {
        const response = await api.put(`/api/logs/security/alerts/${alertId}/read`);
        return response.data;
    } catch (error) {
        console.error('Erro ao marcar alerta como lido:', error);
        throw error;
    }
};

// Buscar alterações de permissões
export const fetchPermissionChanges = async (limit = 50) => {
    try {
        const response = await api.get(`/api/logs/security/permission-changes?limit=${limit}`);
        return response.data;
    } catch (error) {
        console.error('Erro ao buscar alterações de permissões:', error);
        throw error;
    }
};

// ============================================
// SESSIONS API
// ============================================

// Buscar sessões ativas
export const fetchActiveSessions = async () => {
    try {
        const response = await api.get('/api/logs/sessions');
        return response.data;
    } catch (error) {
        console.error('Erro ao buscar sessões ativas:', error);
        throw error;
    }
};

// Buscar sessões simultâneas
export const fetchConcurrentSessions = async () => {
    try {
        const response = await api.get('/api/logs/sessions/concurrent');
        return response.data;
    } catch (error) {
        console.error('Erro ao buscar sessões simultâneas:', error);
        throw error;
    }
};

// Buscar último acesso dos usuários
export const fetchUsersLastAccess = async () => {
    try {
        const response = await api.get('/api/logs/users/last-access');
        return response.data;
    } catch (error) {
        console.error('Erro ao buscar último acesso:', error);
        throw error;
    }
};

// Buscar estatísticas de dispositivos
export const fetchDeviceStats = async (days = 30) => {
    try {
        const response = await api.get(`/api/logs/users/devices?days=${days}`);
        return response.data;
    } catch (error) {
        console.error('Erro ao buscar estatísticas de dispositivos:', error);
        throw error;
    }
};

// ============================================
// PERFORMANCE API
// ============================================

// Buscar endpoints lentos
export const fetchSlowEndpoints = async (minDuration = 1000, days = 7, limit = 20) => {
    try {
        const response = await api.get(`/api/logs/performance/slow-endpoints?minDuration=${minDuration}&days=${days}&limit=${limit}`);
        return response.data;
    } catch (error) {
        console.error('Erro ao buscar endpoints lentos:', error);
        throw error;
    }
};

// Buscar erros agrupados
export const fetchErrorsByType = async (days = 7, limit = 20) => {
    try {
        const response = await api.get(`/api/logs/performance/errors?days=${days}&limit=${limit}`);
        return response.data;
    } catch (error) {
        console.error('Erro ao buscar erros:', error);
        throw error;
    }
};

// Buscar métricas do servidor
export const fetchServerMetrics = async (hours = 24) => {
    try {
        const response = await api.get(`/api/logs/performance/server-metrics?hours=${hours}`);
        return response.data;
    } catch (error) {
        console.error('Erro ao buscar métricas do servidor:', error);
        throw error;
    }
};

// ============================================
// ANALYTICS API
// ============================================

// Buscar resumo de analytics
export const fetchAnalyticsSummary = async () => {
    try {
        const response = await api.get('/api/logs/analytics/summary');
        return response.data;
    } catch (error) {
        console.error('Erro ao buscar resumo de analytics:', error);
        throw error;
    }
};

// Buscar produtos mais visualizados
export const fetchTopProducts = async (days = 30, limit = 20) => {
    try {
        const response = await api.get(`/api/logs/analytics/top-products?days=${days}&limit=${limit}`);
        return response.data;
    } catch (error) {
        console.error('Erro ao buscar produtos mais visualizados:', error);
        throw error;
    }
};

// Buscar funcionalidades mais usadas
export const fetchTopFeatures = async (days = 30, limit = 20) => {
    try {
        const response = await api.get(`/api/logs/analytics/top-features?days=${days}&limit=${limit}`);
        return response.data;
    } catch (error) {
        console.error('Erro ao buscar funcionalidades mais usadas:', error);
        throw error;
    }
};

// Buscar horários de pico
export const fetchPeakHours = async (days = 7) => {
    try {
        const response = await api.get(`/api/logs/analytics/peak-hours?days=${days}`);
        return response.data;
    } catch (error) {
        console.error('Erro ao buscar horários de pico:', error);
        throw error;
    }
};

// Buscar tendência de uso
export const fetchUsageTrend = async (days = 14) => {
    try {
        const response = await api.get(`/api/logs/analytics/usage-trend?days=${days}`);
        return response.data;
    } catch (error) {
        console.error('Erro ao buscar tendência de uso:', error);
        throw error;
    }
};

// Buscar comparativo de uso
export const fetchUsageComparison = async () => {
    try {
        const response = await api.get('/api/logs/analytics/usage-comparison');
        return response.data;
    } catch (error) {
        console.error('Erro ao buscar comparativo de uso:', error);
        throw error;
    }
};

// Buscar timeline do usuário
export const fetchUserTimeline = async (userId, days = 7) => {
    try {
        const response = await api.get(`/api/logs/user/${userId}/timeline?days=${days}`);
        return response.data;
    } catch (error) {
        console.error('Erro ao buscar timeline do usuário:', error);
        throw error;
    }
};
